// 路由权限管理器：根据登录角色生成权限集合，并过滤不可访问的路由与菜单。
import {
  ROLE_ROUTE_PERMISSION_MAP,
  SUPER_ROLE_KEYS,
} from "./role-route-permission-map";

type PermissionRouteMeta = {
  permissionKey?: string | string[];
};

type PermissionRouteRecord = {
  meta?: PermissionRouteMeta;
  children?: PermissionRouteRecord[];
  component?: unknown;
  [key: string]: unknown;
};

const SUPER_ROLE_SET = new Set<string>(SUPER_ROLE_KEYS);
// 所有已登录角色都需要的最小 Office 落地权限，其他功能仍按角色映射控制。
const OFFICE_LANDING_PERMISSIONS = ["office", "office:workbench"];

export class RoutePermissionManager {
  private static instance: RoutePermissionManager;

  private userRoutePermissions: Set<string> = new Set();
  private userRoles: string[] = [];
  private isInitialized = false;

  private constructor() {}

  static getInstance(): RoutePermissionManager {
    if (!RoutePermissionManager.instance) {
      RoutePermissionManager.instance = new RoutePermissionManager();
    }
    return RoutePermissionManager.instance;
  }

  /**
   * 根据用户角色初始化权限集合，并补齐统一登录所需的 Office 最小权限。
   * @param userRoles 当前用户角色编码列表
   */
  initRoutePermissions(userRoles: string[]): void {
    this.userRoutePermissions.clear();
    this.userRoles = userRoles || [];
    this.isInitialized = false;

    if (!userRoles?.length) {
      return;
    }

    OFFICE_LANDING_PERMISSIONS.forEach((permission) =>
      this.userRoutePermissions.add(permission)
    );
    userRoles.forEach((role) => {
      const rolePermissions = ROLE_ROUTE_PERMISSION_MAP[role] || [];
      rolePermissions.forEach((permission) =>
        this.userRoutePermissions.add(permission)
      );
    });

    this.isInitialized = true;
  }

  hasRoutePermission(permissionKey: string): boolean {
    if (!this.isReady()) {
      return false;
    }
    if (this.isSuperRole()) {
      return true;
    }
    return this.userRoutePermissions.has(permissionKey);
  }

  hasAnyRoutePermission(permissionKeys: string[]): boolean {
    if (!this.isReady()) {
      return false;
    }
    if (this.isSuperRole()) {
      return true;
    }
    return permissionKeys.some((key) => this.userRoutePermissions.has(key));
  }

  hasAllRoutePermissions(permissionKeys: string[]): boolean {
    if (!this.isReady()) {
      return false;
    }
    if (this.isSuperRole()) {
      return true;
    }
    return permissionKeys.every((key) => this.userRoutePermissions.has(key));
  }

  checkRouteAccess(_path: string, route?: any): boolean {
    if (!this.isReady()) {
      return false;
    }
    if (this.isSuperRole()) {
      return true;
    }

    const permissionKey = route?.meta?.permissionKey;
    if (!permissionKey) {
      return true;
    }

    if (Array.isArray(permissionKey)) {
      return this.hasAnyRoutePermission(permissionKey);
    }
    return this.hasRoutePermission(permissionKey);
  }

  getUserRoutePermissions(): string[] {
    return Array.from(this.userRoutePermissions);
  }

  getUserRoles(): string[] {
    return [...this.userRoles];
  }

  filterRoutesByPermission(
    routes: PermissionRouteRecord[]
  ): PermissionRouteRecord[] {
    if (!this.isReady()) {
      return [];
    }
    if (this.isSuperRole()) {
      return routes;
    }

    return routes
      .map((route) => this.filterSingleRoute(route))
      .filter((route): route is PermissionRouteRecord => Boolean(route));
  }

  filterMenuByPermission(
    menuItems: PermissionRouteRecord[]
  ): PermissionRouteRecord[] {
    return this.filterRoutesByPermission(menuItems);
  }

  resetPermissions(): void {
    this.userRoutePermissions.clear();
    this.userRoles = [];
    this.isInitialized = false;
  }

  isPermissionInitialized(): boolean {
    return this.isInitialized;
  }

  private isReady(): boolean {
    if (this.isInitialized) {
      return true;
    }
    return import.meta.env.DEV;
  }

  private isSuperRole(): boolean {
    return this.userRoles.some((role) => SUPER_ROLE_SET.has(role));
  }

  private filterSingleRoute(
    route: PermissionRouteRecord
  ): PermissionRouteRecord | null {
    const clonedRoute: PermissionRouteRecord = {
      ...route,
      children: route.children ? [...route.children] : undefined,
    };

    if (clonedRoute.children?.length) {
      const filteredChildren = clonedRoute.children
        .map((child) => this.filterSingleRoute(child))
        .filter((child): child is PermissionRouteRecord => Boolean(child));
      clonedRoute.children = filteredChildren;
    }

    const permissionKey = clonedRoute.meta?.permissionKey;
    const hasSelfPermission = permissionKey
      ? Array.isArray(permissionKey)
        ? this.hasAnyRoutePermission(permissionKey)
        : this.hasRoutePermission(permissionKey)
      : true;

    // 父级无模块权限时，若仍有可访问子页，保留父级作为菜单容器
    if (!hasSelfPermission) {
      if (clonedRoute.children?.length) {
        return clonedRoute;
      }
      return null;
    }

    if (
      clonedRoute.children &&
      !clonedRoute.children.length &&
      !clonedRoute.component
    ) {
      return null;
    }

    return clonedRoute;
  }
}
